import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../translations';
import SafeIcon from '../components/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSend, FiCheckCircle, FiBriefcase, FiBarChart2, FiUsers, FiTrendingUp, FiDollarSign, FiAward, FiGlobe } = FiIcons;

const Partnership = () => {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    proposal: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const benefits = [
    {
      icon: FiDollarSign,
      title: 'Revenue Sharing',
      description: 'Earn up to 30% recurring commission on every client you refer',
      color: 'from-green-500 to-emerald-600'
    },
    {
      icon: FiTrendingUp,
      title: 'Growth Support',
      description: 'Access to marketing materials, co-branding, and dedicated support',
      color: 'from-blue-500 to-cyan-600'
    },
    {
      icon: FiAward,
      title: 'Priority Access',
      description: 'Early access to new features and exclusive partner-only benefits',
      color: 'from-purple-500 to-pink-600'
    },
    {
      icon: FiGlobe,
      title: 'Global Network',
      description: 'Join a worldwide network of successful trading professionals',
      color: 'from-orange-500 to-red-600'
    }
  ];

  const stats = [
    { value: '500+', label: 'Active Partners' },
    { value: '$2M+', label: 'Partner Earnings' },
    { value: '50+', label: 'Countries' },
    { value: '98%', label: 'Satisfaction' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black text-white">
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 -left-4 w-96 h-96 bg-purple-500/20 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
        <div className="absolute top-40 -right-4 w-96 h-96 bg-cyan-500/20 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
      </div>

      <main className="relative pt-8 pb-20">
        {/* Hero Section */}
        <div className="container mx-auto px-4 py-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl md:text-7xl font-black mb-6">
              <span className="bg-gradient-to-r from-purple-400 via-cyan-400 to-pink-400 bg-clip-text text-transparent">
                Partner With Snap Chart AI
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto mb-8">
              Join our growing network of partners and unlock new revenue streams
            </p>
          </motion.div>

          {/* Stats Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto mt-12"
          >
            {stats.map((stat, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                <div className="text-3xl md:text-4xl font-black bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-400">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Platform Preview */}
        <div className="container mx-auto px-4 py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600/30 to-cyan-600/30 rounded-3xl blur-3xl"></div>
            <div className="relative bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-xl border border-white/20 rounded-3xl p-8 overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-purple-500/20 to-transparent rounded-full blur-3xl"></div>
              
              {/* Mock Platform Screenshot */}
              <div className="relative bg-gray-950 rounded-2xl border border-white/10 overflow-hidden shadow-2xl">
                <div className="bg-gray-900 px-4 py-3 flex items-center gap-2 border-b border-white/10">
                  <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div className="flex-1 text-center text-sm text-gray-400">Snap Chart AI Trading Platform</div>
                </div>
                
                <div className="p-8 space-y-6">
                  {/* Mock Chart */}
                  <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 border border-white/5">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <div className="text-2xl font-bold">XAUUSD</div>
                        <div className="text-green-400 text-sm">+0.31% • $4,075.80</div>
                      </div>
                      <div className="flex gap-2">
                        <div className="px-3 py-1 bg-purple-500/20 rounded-lg text-sm">1H</div>
                        <div className="px-3 py-1 bg-gray-700 rounded-lg text-sm">4H</div>
                        <div className="px-3 py-1 bg-gray-700 rounded-lg text-sm">1D</div>
                      </div>
                    </div>
                    <div className="h-48 bg-gradient-to-br from-purple-500/5 to-cyan-500/5 rounded-lg flex items-end justify-around p-4">
                      {[65, 80, 45, 90, 70, 85, 60, 95, 75, 88].map((height, i) => (
                        <div key={i} className="w-8 bg-gradient-to-t from-purple-600 to-cyan-600 rounded-t" style={{ height: `${height}%` }}></div>
                      ))}
                    </div>
                  </div>

                  {/* Mock AI Analysis */}
                  <div className="bg-gradient-to-br from-purple-500/10 to-cyan-500/10 rounded-xl p-6 border border-purple-500/20">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-gradient-to-r from-purple-600 to-cyan-600 rounded-lg">
                        <SafeIcon icon={FiBarChart2} className="text-xl" />
                      </div>
                      <div>
                        <div className="font-bold">AI Analysis Result</div>
                        <div className="text-sm text-gray-400">Bloomberg Terminal Level</div>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm text-gray-300">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        <span>Strong support at $4,050 with 85% probability</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                        <span>Resistance zone $4,100-$4,120</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                        <span>R:R ratio 1:3.2 for long positions</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-center mt-8">
                <p className="text-gray-400 text-lg">
                  🎯 <span className="text-white font-semibold">Your clients get access to this powerful platform</span>
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Benefits Section */}
        <div className="container mx-auto px-4 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-black mb-4">
              <span className="bg-gradient-to-r from-white to-cyan-400 bg-clip-text text-transparent">
                Partnership Benefits
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Everything you need to succeed as a Snap Chart AI partner
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative"
              >
                <div className={`absolute inset-0 bg-gradient-to-r ${benefit.color} rounded-2xl blur-xl opacity-20 group-hover:opacity-40 transition-opacity`}></div>
                <div className="relative bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-xl border border-white/20 rounded-2xl p-8 hover:border-white/40 transition-all">
                  <div className={`inline-flex p-4 bg-gradient-to-r ${benefit.color} rounded-2xl mb-4`}>
                    <SafeIcon icon={benefit.icon} className="text-3xl text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3">{benefit.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{benefit.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Application Form */}
        <div className="container mx-auto px-4 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600/30 to-cyan-600/30 rounded-3xl blur-2xl"></div>
              
              <div className="relative bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-xl border border-white/20 rounded-3xl p-8 md:p-12">
                <div className="text-center mb-8">
                  <h2 className="text-3xl md:text-4xl font-black mb-4">
                    <span className="bg-gradient-to-r from-white to-cyan-400 bg-clip-text text-transparent">
                      Apply for Partnership
                    </span>
                  </h2>
                  <p className="text-gray-400">Start earning with Snap Chart AI today</p>
                </div>

                {submitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-12"
                  >
                    <div className="inline-flex p-6 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full mb-6">
                      <SafeIcon icon={FiCheckCircle} className="text-6xl text-white" />
                    </div>
                    <h3 className="text-2xl font-bold mb-3">Application Submitted!</h3>
                    <p className="text-gray-400 max-w-md mx-auto">
                      Thank you for your interest. Our partnership team will review your application and contact you within 48 hours.
                    </p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                          Company Name
                        </label>
                        <input
                          type="text"
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleChange}
                          required
                          className="w-full bg-gray-900/50 border border-white/10 rounded-xl py-3.5 px-4 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/20 transition-all"
                          placeholder="Your company name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                          Contact Name
                        </label>
                        <input
                          type="text"
                          name="contactName"
                          value={formData.contactName}
                          onChange={handleChange}
                          required
                          className="w-full bg-gray-900/50 border border-white/10 rounded-xl py-3.5 px-4 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/20 transition-all"
                          placeholder="Your full name"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">
                        Business Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full bg-gray-900/50 border border-white/10 rounded-xl py-3.5 px-4 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/20 transition-all"
                        placeholder="business@company.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">
                        Tell us about your business
                      </label>
                      <textarea
                        name="proposal"
                        value={formData.proposal}
                        onChange={handleChange}
                        required
                        rows="6"
                        className="w-full bg-gray-900/50 border border-white/10 rounded-xl py-3.5 px-4 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/20 transition-all resize-none"
                        placeholder="Describe your business, audience, and why you'd like to partner with Snap Chart AI..."
                      ></textarea>
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      className="w-full bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-500 hover:to-cyan-500 text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-purple-500/50"
                    >
                      <SafeIcon icon={FiSend} className="text-lg" />
                      <span>Submit Application</span>
                    </motion.button>
                  </form>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      <style jsx>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(20px, -50px) scale(1.1); }
          50% { transform: translate(-20px, 20px) scale(0.9); }
          75% { transform: translate(50px, 50px) scale(1.05); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
      `}</style>
    </div>
  );
};

export default Partnership;